import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const two = (x) => (x < 20 ? ONES[x] : TENS[Math.floor(x / 10)] + (x % 10 ? " " + ONES[x % 10] : ""));
const three = (x) => (x >= 100 ? `${ONES[Math.floor(x / 100)]} Hundred${x % 100 ? " " + two(x % 100) : ""}` : two(x));
export const inWords = (num) => {
  let n = Math.round(Math.abs(num || 0));
  const parts = [];
  const cr = Math.floor(n / 1e7); n %= 1e7;
  const lk = Math.floor(n / 1e5); n %= 1e5;
  const th = Math.floor(n / 1000); n %= 1000;
  if (cr) parts.push(`${three(cr)} Crore`);
  if (lk) parts.push(`${two(lk)} Lakh`);
  if (th) parts.push(`${two(th)} Thousand`);
  if (n) parts.push(three(n));
  return `${parts.join(" ") || "Zero"} Rupees`;
};

const fmtDate = (s) => { if (!s) return ""; const [y, m, d] = String(s).slice(0, 10).split("-"); return d ? `${d}/${m}/${y}` : s; };
const n2 = (v) => (Number(v) || 0).toFixed(2);

export const printPdfBlob = (blob) => {
  const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
  const old = document.getElementById("pdf-print-frame"); if (old) old.remove();
  const f = document.createElement("iframe");
  f.id = "pdf-print-frame"; f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  f.src = url;
  f.onload = () => setTimeout(() => { try { f.contentWindow.focus(); f.contentWindow.print(); } catch { window.open(url, "_blank"); toast.message("Use the PDF viewer's print button"); } }, 300);
  document.body.appendChild(f);
};

export const invStatus = (inv) => {
  if (inv.status === "cancelled") return ["Cancelled", "bg-neutral-500/15 text-muted-foreground"];
  if (inv.status === "paid") return ["Paid", "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"];
  if (inv.sent_at || inv.status === "partial") return [inv.status === "partial" ? "Sent · part paid" : "Sent", "bg-sky-500/15 text-sky-700 dark:text-sky-400"];
  return ["Draft", "bg-amber-500/15 text-amber-700 dark:text-amber-400"];
};

export const invoiceTotals = (d) => {
  const subtotal = d.items.reduce((a, x) => a + (parseFloat(x.amount) || 0), 0);
  const tax = d.type === "tax" && d.gstEnabled ? subtotal * (parseFloat(d.gstPercent) || 0) / 100 : 0;
  const total = subtotal + tax;
  const net = Math.round(total);
  return { subtotal, tax, total, net, roundOff: net - total };
};

const F = ({ v, on, edit, className = "", type = "text", testid, placeholder }) => (edit
  ? <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} placeholder={placeholder} className={`bg-amber-50 border border-amber-300 px-1 py-0.5 outline-none focus:border-amber-600 w-full ${className}`} data-testid={testid} />
  : <span className={className}>{type === "date" ? fmtDate(v) : type === "number" ? n2(v) : v}</span>);

const Header = ({ cs, d }) => (
  <>
    <div className="border border-neutral-500 grid grid-cols-[1fr_auto_1.15fr] items-center p-3 gap-4">
      <img src="/lr-logo.jpg?v=2" alt="Arrow" className="h-16 sm:h-20 object-contain justify-self-center" />
      <div className="w-[3px] self-stretch bg-black" />
      <div className="text-[11px] leading-snug">
        <div className="text-sm font-bold text-[#2B3990]">{cs.company_name}</div>
        <div>{cs.phones}</div><div>{cs.email}</div><div>{cs.website}</div>
        <div className="mt-1">{cs.address}</div>
      </div>
    </div>
    <div className="border-x border-b border-neutral-500 grid grid-cols-3 text-[12px] font-bold px-2 py-1">
      <span>GST No. : {cs.gst_no}</span><span className="text-center">Original</span><span className="text-right">Branch State : {cs.branch_state}</span>
    </div>
    <div className="border-x border-b border-neutral-500 grid grid-cols-[1fr_1fr_1fr_1.35fr] text-[11px] font-bold">
      <span className="px-2 py-1 border-r border-neutral-500">PAN No. : {cs.pan_no}</span><span className="border-r border-neutral-500" />
      <span className="px-2 py-1 border-r border-neutral-500 text-center text-sm" data-testid="inv-doc-label">{d.type === "tax" ? "Tax Invoice" : "Bill of Supply"}</span>
      <span className="px-2 py-1 text-right">Services Accounting Code : {cs.sac_code}</span>
    </div>
  </>
);

const Party = ({ d, edit, on }) => {
  const b = d.billTo;
  const bt = (k) => (x) => on.billTo(k, x);
  return (
    <div className="border border-neutral-500 grid grid-cols-1 sm:grid-cols-[1.9fr_1.1fr_0.8fr] mt-2 text-[12px]">
      <div className="p-2 space-y-0.5 sm:border-r border-neutral-500">
        <div>To,</div>
        <div className="font-bold text-sm"><F edit={edit} v={b.company || b.name} on={(x) => { bt("company")(x); if (!b.name || b.name === b.company) bt("name")(x); }} testid="inv-page-billto-name" placeholder="Customer name" /></div>
        <div className="text-[11px]"><F edit={edit} v={b.address} on={bt("address")} testid="inv-page-billto-address" placeholder="Address" /></div>
        <div className="text-[11px] flex flex-wrap gap-1 items-center">
          {edit ? <><F edit v={b.city} on={bt("city")} className="!w-32" placeholder="City" testid="inv-page-billto-city" /><F edit v={b.state} on={bt("state")} className="!w-36" placeholder="State" testid="inv-page-billto-state" /><F edit v={b.gst_number} on={bt("gst_number")} className="!w-44" placeholder="GST No." testid="inv-page-billto-gst" /></>
            : <span>GST No. : {b.gst_number || "NA"} - State : {(b.state || "").toUpperCase()}</span>}
        </div>
      </div>
      <div className="p-2 sm:border-r border-t sm:border-t-0 border-neutral-500"><div>Invoice No.</div><div className="font-bold text-sm" data-testid="inv-page-serial">{d.serial || "Auto on save"}</div></div>
      <div className="p-2 border-t sm:border-t-0 border-neutral-500"><div>Date</div><div className="font-bold text-sm"><F edit={edit} type="date" v={d.invoiceDate} on={on.date} testid="inv-page-date" /></div></div>
    </div>
  );
};

const COLS = [["lr_number", "LR NO.", "w-24"], ["date", "Date", "w-28", "date"], ["vehicle_no", "Vehicle No.", "w-28"], ["from_city", "From", "w-32"], ["to_city", "To", "w-32"], ["description", "Particulars", "min-w-[220px]"]];

const Rows = ({ d, edit, on }) => (
  <div className="border-x border-neutral-500 overflow-x-auto">
    <table className="w-full text-[11px] border-collapse">
      <thead><tr className="font-bold border-b border-neutral-500">
        <th className="border-r border-neutral-500 px-1 py-2 w-10">SR. NO.</th>
        {COLS.map(([k, l, w]) => <th key={k} className={`border-r border-neutral-500 px-1 ${w}`}>{l}</th>)}
        <th className="px-1 w-28">Freight</th>{edit && <th className="w-8" />}
      </tr></thead>
      <tbody>
        {d.items.map((it, i) => (
          <tr key={i} className="border-b border-neutral-300 align-top" data-testid={`inv-page-row-${i}`}>
            <td className="border-r border-neutral-500 text-center py-1.5">{i + 1}</td>
            {COLS.map(([k, , , type]) => (
              <td key={k} className={`border-r border-neutral-500 px-1 py-1 ${k === "description" || k.endsWith("city") ? "" : "text-center"} ${k.endsWith("city") ? "uppercase" : ""}`}>
                <F edit={edit} type={type} v={it[k]} on={(x) => on.item(i, { [k]: x })} testid={`inv-page-${k}-${i}`} />
              </td>
            ))}
            <td className="px-1 py-1 text-right font-mono-num"><F edit={edit} type="number" v={it.amount} on={(x) => on.item(i, { amount: x })} className="text-right" testid={`inv-page-amount-${i}`} />{!edit && ""}</td>
            {edit && <td><button onClick={() => on.removeRow(i)} className="p-1 text-neutral-500 hover:text-red-600" data-testid={`inv-page-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button></td>}
          </tr>
        ))}
        <tr><td className="border-r border-neutral-500 h-40" />{COLS.map(([k]) => <td key={k} className="border-r border-neutral-500" />)}<td />{edit && <td />}</tr>
      </tbody>
    </table>
    {edit && (
      <div className="m-2 flex gap-4">
        <button onClick={on.addRow} className="inline-flex items-center gap-1 text-[11px] font-bold uppercase text-[#2B3990] hover:underline" data-testid="inv-page-add-row"><Plus className="h-3.5 w-3.5" /> Add row</button>
        <button onClick={on.addExtra} className="inline-flex items-center gap-1 text-[11px] font-bold uppercase text-[#2B3990] hover:underline" data-testid="inv-page-add-extra"><Plus className="h-3.5 w-3.5" /> Add extra charge</button>
      </div>
    )}
  </div>
);

const Totals = ({ d }) => {
  const t = invoiceTotals(d);
  const pct = parseFloat(d.gstPercent) || 0;
  const rows = [["Sub Total (₹)", t.subtotal]];
  if (t.tax > 0) {
    if (d.gstMode === "igst") rows.push([`IGST @ ${pct}%`, t.tax]);
    else rows.push([`CGST @ ${pct / 2}%`, t.tax / 2], [`SGST @ ${pct / 2}%`, t.tax / 2]);
  }
  rows.push(["Round Off", t.roundOff]);
  return (
    <div className="border border-neutral-500 text-[11px]">
      {rows.map(([l, v]) => (
        <div key={l} className="grid grid-cols-[1fr_190px_110px] sm:grid-cols-[1fr_200px_120px]"><span className="border-r border-neutral-500" /><span className="border-r border-b border-neutral-500 px-2 py-1 font-bold">{l}</span><span className="border-b border-neutral-500 px-2 py-1 text-right font-bold font-mono-num">{n2(v)}</span></div>
      ))}
      <div className="grid grid-cols-[1fr_190px_110px] sm:grid-cols-[1fr_200px_120px]">
        <span className="border-r border-neutral-500 px-2 py-1 font-bold" data-testid="inv-page-words">Amount In Words : {inWords(t.net)}</span>
        <span className="border-r border-neutral-500 px-2 py-1 font-bold">Net Amount (₹)</span>
        <span className="px-2 py-1 text-right font-bold text-sm font-mono-num" data-testid="inv-page-net">{n2(t.net)}</span>
      </div>
    </div>
  );
};

const Footer = ({ cs, d, edit, on, sigUrl }) => (
  <>
    <div className="border-x border-b border-neutral-500 grid grid-cols-1 sm:grid-cols-2 p-2 text-[10px] gap-3 min-h-[150px]">
      <div className="space-y-0.5">
        <div className="font-bold">Terms and Conditions</div>
        {(cs.terms || "").split("\n").map((l, i) => <div key={i}>{l}</div>)}
        <div className="pt-3">BANK DETAILS :</div>
        <div className="font-bold uppercase">BANK NAME : {cs.bank_name}</div>
        <div className="font-bold uppercase">BANK AC NO. {cs.bank_ac_no}</div>
        {(cs.bank_branch || "").split("\n").map((l, i) => <div key={i} className="font-bold uppercase">{i === 0 ? `BRANCH : ${l}` : l}</div>)}
        <div className="font-bold uppercase">IFSC CODE: {cs.ifsc}</div>
        <div className="font-bold uppercase">ACCOUNT TYPE : {cs.account_type}</div>
      </div>
      <div className="flex flex-col justify-between items-end text-[11px]">
        <div>For, <b>ARROW INTERNATIONAL PACKERS &amp; MOVERS</b></div>
        {sigUrl && <img src={sigUrl} alt="Signature" className="h-14 max-w-[180px] object-contain" data-testid="inv-page-signature-img" />}
        <div className="font-bold">Authorised Signatory</div>
      </div>
    </div>
    <div className="border-x border-b border-neutral-500 grid grid-cols-3 p-2 text-[10px] items-center">
      <span>Received By</span>
      <span className="text-center">Prepared By<br /><F edit={edit} v={d.preparedBy} on={on.preparedBy} className="text-center" testid="inv-page-prepared-by" /></span>
      <span className="text-right">Checked By</span>
    </div>
    <div className="border-x border-b border-neutral-500 h-8" />
  </>
);

const CancelStamp = ({ inv }) => (
  <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-10" data-testid="inv-cancelled-stamp">
    <div className="-rotate-[28deg] border-[6px] border-red-600/60 rounded-xl px-8 py-2 text-center text-red-600/50">
      <div className="font-black text-6xl sm:text-8xl tracking-widest">CANCELLED</div>
      {inv?.cancel_reason && <div className="text-xs sm:text-sm font-bold text-red-700/80 max-w-md mx-auto">Reason: {inv.cancel_reason}</div>}
    </div>
  </div>
);

export function InvoicePaper({ d, cs, edit = false, on = {}, sigUrl = "", inv = null }) {
  return (
    <div className="relative bg-white text-black w-full max-w-[920px] mx-auto p-3 sm:p-6 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.35)] font-[Arial,Helvetica,sans-serif]" data-testid="invoice-paper">
      {inv?.status === "cancelled" && <CancelStamp inv={inv} />}
      <Header cs={cs} d={d} />
      <Party d={d} edit={edit} on={on} />
      <Rows d={d} edit={edit} on={on} />
      <Totals d={d} />
      <Footer cs={cs} d={d} edit={edit} on={on} sigUrl={sigUrl} />
    </div>
  );
}
